import { useField, useFormikContext } from 'formik'
import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import styled from 'styled-components'
import placeholderImg from 'assets/mugshot-placeholder.png'
import { FormGroup } from 'components'
import Icon from 'components/Icon'

import { _, colors } from 'utils'
import { $white } from 'utils/colors'
import { MugShotFieldProps } from '../types'

const S = {
    Wrapper: styled.div`
        display: flex;
    `,
    ThumbnailContainer: styled.div`
        position: relative;
        margin-right: 8px;
    `,
    Thumbnail: styled.img`
        height: 64px;
        width: 64px;
        object-fit: cover;
        border-radius: 50%;
    `,
    LabelContainer: styled.div`
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        
        > label {
            font-size: 16px;
        }
        > small {
            font-size: 14px;
        }
    `,
    UploadButton: styled.div`
        position: absolute;
        right: 0;
        bottom: 0;
        width: 24px;
        height: 24px;
        background: ${colors.primary};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    HiddenInput: styled(Form.File)`
        opacity: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
    `,
}

const MugShotField = ({
    name,
    label,
    helperText,
    placeholder = '',
    isRequired,
    accept,
    maxSize = 20,
    dependField,
    isDisabled,
    isBase64,
    ...props
}: MugShotFieldProps) => {
    const [
        field,
        meta,
        helper,
    ] = useField({ name })
    const { setFieldValue } = useFormikContext()
    const [previewImg, setPreviewImg] = useState(field.value)
    const { error, touched } = meta

    const handleChange = (e) => {
        helper.setTouched(true)
        if (e.currentTarget?.files[0]?.size > maxSize * 1024 * 1024) {
            helper.setError(`Max size of file is ${maxSize} Mbs`)
            return
        }

        const reader = new FileReader()
        reader.onload = (fileReaderEvent) => {
            const base64Img = fileReaderEvent.target.result
            setPreviewImg(base64Img)
            if (isBase64) {
                setFieldValue(dependField, base64Img)
            }
        }

        reader.readAsDataURL(e.currentTarget.files[0])
        helper.setValue(e.currentTarget.files[0])
    }

    return (
        <FormGroup
            controlId={`${field.name}-file`}
            {...props}
        >
            <S.Wrapper>
                <S.ThumbnailContainer>
                    <S.Thumbnail src={previewImg || placeholderImg} alt="" />
                    <Form.Label className="mb-0">
                        <S.UploadButton className="text-cut">
                            <Icon icon="edit" size={16} color={$white} />
                        </S.UploadButton>
                    </Form.Label>
                </S.ThumbnailContainer>
                <S.LabelContainer>
                    <Form.Label>{label}</Form.Label>
                    {!(touched && error) && helperText && (
                        <Form.Text muted className="mt-0">{helperText}</Form.Text>
                    )}

                    {touched && error && (
                        <small className="text-danger invalid-field">
                            {_(error)}
                        </small>
                    )}
                </S.LabelContainer>
            </S.Wrapper>
            <S.HiddenInput
                id={`${field.name}-file`}
                label={placeholder}
                value=""
                name={field.name}
                multiple={field.multiple}
                accept="image/*"
                onChange={handleChange}
                custom
            />
        </FormGroup>
    )
}

export default MugShotField
